import React, { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { ff } from '../../../theme';

interface UploadProgressProps {
  isSubmitting: boolean;
  viewOnly: boolean;
  onProgress: (progress: number) => void;
}

export interface UploadProgressRef {
  updateProgress: (progress: number) => void;
}

const UploadProgress = forwardRef<UploadProgressRef, UploadProgressProps>(({ isSubmitting, viewOnly, onProgress }, ref) => {
  const [progress, setProgress] = useState(0);

  // This callback will be stable and won't cause parent re-renders
  const handleProgress = useCallback((newProgress: number) => {
    setProgress(newProgress);
    onProgress(newProgress);
  }, [onProgress]);

  // Expose the progress handler
  useImperativeHandle(ref, () => ({
    updateProgress: handleProgress
  }));

  if (!isSubmitting || viewOnly) return null;

  return (
    <VStack role="status" spacing="8px" w="100%" align="stretch" px="14px" py="12px" borderRadius="14px" border="1px solid" borderColor={ff.border}>
      {progress > 0 && (
        <>
          <Flex justify="space-between" fontSize="14px" fontWeight={600} color={ff.ink}>
            <Text as="span">Uploading</Text>
            <Text as="span">{Math.round(progress)}%</Text>
          </Flex>
          <Box h="6px" borderRadius="full" bg={ff.line} overflow="hidden">
            <Box h="6px" w={`${Math.round(progress)}%`} bg={ff.ink} transition="width 0.2s" />
          </Box>
        </>
      )}
      <Text color={ff.text2} fontSize="13px" lineHeight={1.4}>
        Large files might take a while to upload, please don't leave the page.
      </Text>
    </VStack>
  );
});

UploadProgress.displayName = 'UploadProgress';

export default React.memo(UploadProgress);